import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Facebook, Linkedin, MessageCircle, Phone, Mail } from "lucide-react";
import { useLang } from "../lib/i18n";
import { PROFILE } from "../lib/profile";
import useReveal from "../hooks/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
    const { t } = useLang();
    const { ref, visible } = useReveal();
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
        consent: false,
    });
    const [loading, setLoading] = useState(false);

    const update = (field) => (e) => {
        const value =
            e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((f) => ({ ...f, [field]: value }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.consent) {
            toast.error(t.contact.form.consentRequired);
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success(t.contact.form.success);
            setForm({
                name: "",
                email: "",
                phone: "",
                subject: "",
                message: "",
                consent: false,
            });
        } catch (err) {
            console.error(err);
            toast.error(t.contact.form.error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="contact"
            ref={ref}
            className={`relative py-24 md:py-32 border-t border-navy/10 reveal ${
                visible ? "visible" : ""
            }`}
            data-testid="contact-section"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
                {/* Left — info + social */}
                <div className="md:col-span-5">
                    <div className="flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-navy/60">
                        <span className="tick" />
                        {t.contact.kicker}
                    </div>
                    <h2
                        className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05] tracking-tight"
                        data-testid="contact-heading"
                    >
                        {t.contact.heading}
                    </h2>
                    <p className="mt-6 text-navy/65 text-lg leading-relaxed max-w-md">
                        {t.contact.copy}
                    </p>

                    <div className="mt-12 space-y-1" data-testid="contact-channels">
                        <a
                            href={PROFILE.whatsapp(
                                "Hola Gilberto, te contacto desde tu sitio web.",
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex items-center justify-between py-5 border-t border-navy/15 hover:pl-3 transition-all"
                            data-testid="whatsapp-link"
                        >
                            <div className="flex items-center gap-5">
                                <MessageCircle
                                    className="w-5 h-5 text-accentblue"
                                    strokeWidth={1.5}
                                />
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-navy/50">
                                        {t.contact.whatsapp}
                                    </div>
                                    <div className="font-display text-xl text-navy">
                                        {PROFILE.phoneDisplay}
                                    </div>
                                </div>
                            </div>
                            <span className="text-navy/40 group-hover:text-navy group-hover:translate-x-1 transition-all">
                                →
                            </span>
                        </a>

                        <a
                            href={`tel:${PROFILE.phoneIntl}`}
                            className="group flex items-center justify-between py-5 border-t border-navy/15 hover:pl-3 transition-all"
                            data-testid="phone-link"
                        >
                            <div className="flex items-center gap-5">
                                <Phone
                                    className="w-5 h-5 text-accentblue"
                                    strokeWidth={1.5}
                                />
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-navy/50">
                                        {t.contact.phoneLabel}
                                    </div>
                                    <div className="font-display text-xl text-navy">
                                        {PROFILE.phoneDisplay}
                                    </div>
                                </div>
                            </div>
                            <span className="text-navy/40 group-hover:text-navy group-hover:translate-x-1 transition-all">
                                →
                            </span>
                        </a>

                        <a
                            href={PROFILE.facebook}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex items-center justify-between py-5 border-t border-navy/15 hover:pl-3 transition-all"
                            data-testid="facebook-link"
                        >
                            <div className="flex items-center gap-5">
                                <Facebook
                                    className="w-5 h-5 text-accentblue"
                                    strokeWidth={1.5}
                                />
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-navy/50">
                                        {t.contact.facebook}
                                    </div>
                                    <div className="font-display text-xl text-navy">
                                        /gilbertoevelize
                                    </div>
                                </div>
                            </div>
                            <span className="text-navy/40 group-hover:text-navy group-hover:translate-x-1 transition-all">
                                →
                            </span>
                        </a>

                        <a
                            href={PROFILE.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex items-center justify-between py-5 border-t border-b border-navy/15 hover:pl-3 transition-all"
                            data-testid="linkedin-link"
                        >
                            <div className="flex items-center gap-5">
                                <Linkedin
                                    className="w-5 h-5 text-accentblue"
                                    strokeWidth={1.5}
                                />
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-navy/50">
                                        {t.contact.linkedin}
                                    </div>
                                    <div className="font-display text-xl text-navy">
                                        /in/gilbertovelize
                                    </div>
                                </div>
                            </div>
                            <span className="text-navy/40 group-hover:text-navy group-hover:translate-x-1 transition-all">
                                →
                            </span>
                        </a>
                    </div>
                </div>

                {/* Right — Form */}
                <div className="md:col-span-7">
                    <form
                        onSubmit={submit}
                        className="space-y-6"
                        data-testid="contact-form"
                        noValidate
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.25em] text-navy/60 mb-2">
                                    {t.contact.form.name} *
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={form.name}
                                    onChange={update("name")}
                                    className="editorial-input"
                                    data-testid="input-name"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.25em] text-navy/60 mb-2">
                                    {t.contact.form.email} *
                                </label>
                                <input
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={update("email")}
                                    className="editorial-input"
                                    data-testid="input-email"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.25em] text-navy/60 mb-2">
                                    {t.contact.form.phone}
                                </label>
                                <input
                                    type="tel"
                                    value={form.phone}
                                    onChange={update("phone")}
                                    className="editorial-input"
                                    data-testid="input-phone"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.25em] text-navy/60 mb-2">
                                    {t.contact.form.subject}
                                </label>
                                <input
                                    type="text"
                                    value={form.subject}
                                    onChange={update("subject")}
                                    className="editorial-input"
                                    data-testid="input-subject"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.25em] text-navy/60 mb-2">
                                {t.contact.form.message} *
                            </label>
                            <textarea
                                required
                                rows={4}
                                value={form.message}
                                onChange={update("message")}
                                className="editorial-input"
                                data-testid="input-message"
                            />
                        </div>

                        <label
                            className="flex items-start gap-3 text-sm text-navy/70 cursor-pointer select-none pt-2"
                            data-testid="consent-wrap"
                        >
                            <input
                                type="checkbox"
                                checked={form.consent}
                                onChange={update("consent")}
                                className="mt-1 w-4 h-4 accent-navy"
                                data-testid="input-consent"
                            />
                            <span>
                                {t.contact.form.consent}{" "}
                                <a
                                    href="#privacy"
                                    className="underline underline-offset-2 hover:text-navy"
                                    data-testid="consent-privacy-link"
                                >
                                    {t.privacy.kicker}
                                </a>
                            </span>
                        </label>

                        <div className="pt-4">
                            <button
                                type="submit"
                                disabled={loading}
                                className="group inline-flex items-center gap-3 bg-navy text-cream px-8 py-4 text-sm tracking-wide hover:bg-navy-300 transition-colors disabled:opacity-60"
                                data-testid="submit-btn"
                            >
                                <Mail className="w-4 h-4" strokeWidth={1.5} />
                                {loading
                                    ? t.contact.form.sending
                                    : t.contact.form.submit}
                                <span className="transition-transform group-hover:translate-x-1">
                                    →
                                </span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
};

export default Contact;
